/**
 * Lazy load scripts.
 *
 * @module app/components/DeferredDependencies
 */

import _ from 'lodash';
import { get } from '../../utils';
import LazyLoadingScript from './LazyLoadingScript';
import DeviceDetector from './DeviceDetector';
import MessageBusTopics from '../../common/MessageBusTopics';
import Modernizr from '../../vendor/modernizr/modernizr-custom';

var dependencies = null;

/**
 * Function to load self initialising modules
 * @returns {void}
 *
 * @private
 */
const loadModules = () => {
    import(/* webpackChunkName: "streamingIconsManager" */'./StreamingIconsManager');
};

/**
 * Loads asset(s) defined in given {@param deps} object.
 *
 * @param {Object} deps  dependency object containing 'js'-keyed array of asset
 * locations, and/or 'css'-keyed array of _style_ asset locations.
 *
 * @returns {void}
 *
 * @private
 */
const loadDepAssets = deps => {
    if (deps && typeof deps === 'object') {
        let assets = [];
        if (deps.js) {
            assets = deps.js;
        }

        if (deps.css) {
            assets = assets.concat(deps.css.map(LazyLoadingScript.toCssAsset));
        }

        LazyLoadingScript.loadAll(assets);
    }
};

/**
 * Set dependencies object.
 * Function is called on init process to make sure dependencies variables are defined
 *
 * @returns {void}
 *
 * @private
 */
var setDependencies = function readUrls() {

    var footerUrls = window.footer && window.footer.scripts || [];
    var seoServiceUrls = window.seoService && window.seoService.scripts || [];
    var breadcrumbsV2Urls = window.breadcrumbsV2Data && window.breadcrumbsV2Data.scripts || [];

    // In case any url variable is undefined lazyloading of element will be resolved immediately
    dependencies = {
        loginForm: {
            url: window.loginURL,
        },
        lazyMobileCss: LazyLoadingScript.toCssAsset(window.newMobileFullCss),
        magnificPopup: {
            url: window.magnificPopupUrl,
        },
        onboarding: {
            url: window.onboardingUrl,
        },
        footer: {
            urls: footerUrls,
        },
        seoService: {
            urls: seoServiceUrls,
        },
        breadcrumbsV2: {
            urls: breadcrumbsV2Urls,
        },
        welcomeMessages: {
            url: get(window, 'whStatic.config.welcomeMessages.bundleUrl'),
        },
        onboardingGuide: {
            url: window.onboardingGuideUrl,
        },
    };

    // Please remove once we drop support of iOS 9.2, Android 4.4 and IE11 (cheers!)
    if (
        !Modernizr.picture &&
        window.whStatic.config.fragmentCdnHost &&
        window.whStatic.config.fragmentCdnPath
    ) {

        var pictureFillUrl = window.whStatic.config.fragmentCdnHost +
            window.whStatic.config.fragmentCdnPath +
            '/js/vendor/polyfills/picturefill.min.js';

        dependencies.pictureFill = { url: pictureFillUrl };
    }
};

/**
 * Load script if provided url exists
 *
 * @param {String} url
 * @param {Function} [callback]
 *
 * @returns {void}
 *
 * @private
 */
var load = function load(url, callback) {
    if (url) {

        LazyLoadingScript.load(url, callback);
    }
};

/**
 * Load all script without dependencies
 *
 * @returns {void}
 *
 * @private
 */
var depsLoaded = function depsLoaded() {
    // Please remove once we drop support of iOS 9.2, Android 4.4 and IE11 (cheers!)
    if (dependencies.pictureFill) {
        load(dependencies.pictureFill.url);
    }
};

/**
 * Load whtv player resources.
 *
 * @returns {void}
 *
 * @private
 */
const loadWHTV = () => {

    window.whStatic.config.whTV &&
        window.whStatic.config.whTV.enabled &&
        DeviceDetector.isDesktop() &&
        loadDepAssets(window.WH.sportsbook.frontEndConfig.dependencies.whtv);
};

/**
 * Load video-player resources.
 * Once loaded, it exposes a player constructor in window namespace.
 * E.g. stream-player & whtv depend on it.
 *
 * @returns {void}
 *
 * @private
 */
const loadVideoPlayer = () => {

    loadDepAssets(window.WH.sportsbook.frontEndConfig.dependencies.videoPlayer);
};

/**
 * Load all script without dependencies
 *
 * @returns {void}
 *
 * @public
 */
var init = async function init() {
    if (window.WH.sportsbook.frontEndConfig.lazyloadScripts) {

        setDependencies();
        LazyLoadingScript.loadAll([
            dependencies.loginForm,
            dependencies.lazyMobileCss,
        ], depsLoaded);

        load(dependencies.magnificPopup.url, function () {
            window.WH.messageBus.publishSticky(MessageBusTopics.DEFERRED_LOAD_MAGNIFIC_POPUP);
        });

        LazyLoadingScript.load(dependencies.onboarding.url);
        LazyLoadingScript.load(dependencies.onboardingGuide.url);
        LazyLoadingScript.loadAllOneByOne(dependencies.footer.urls);
        LazyLoadingScript.loadAllOneByOne(dependencies.seoService.urls);
        LazyLoadingScript.loadAllOneByOne(dependencies.breadcrumbsV2.urls);
        if (get(window, 'WH.i18n.locale') === 'en-gb') {
            import(/* webpackChunkName: "WHRadio" */'./WHRadio').then(radio => radio.init());
        }

        // Loading of welcome messages will be triggered after page is loaded (at least after betslip)
        const initBetslipSubscription = window.WH.messageBus.subscribe(MessageBusTopics.BETSLIP_INIT_COMPLETE, () => {
            LazyLoadingScript.load(dependencies.welcomeMessages.url);
            window.WH.messageBus.unsubscribe(initBetslipSubscription);
        });
    }

    loadModules();
    loadVideoPlayer();
    loadWHTV();
};

/**
 * Public functions
 *
 * @type {Object}
 */
var api = {
    reInit: init,
    init: _.once(init),
};

export default api;
